import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { FlyCue } from './types';
import type { ProjectScope } from '../sync/project-scope';
import { markPendingFlyCueWrite } from '../sync/fly-pending-writes';

export interface FlySlice {
    flyCues: FlyCue[];
    flyActions: {
        addFlyCue: (cue: Partial<FlyCue>) => string;
        updateFlyCue: (id: string, updates: Partial<FlyCue>) => void;
        deleteFlyCue: (id: string) => void;
        reorderFlyCues: (sceneId: string, oldIndex: number, newIndex: number) => void;
        clearFly: () => void;
    };
}

function getFlyProjectScope(state: AppState): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

export const createFlySlice: StateCreator<AppState, [['zustand/immer', never]], [], FlySlice> = (set, get) => ({
    flyCues: [],
    flyActions: {
        addFlyCue: (cue) => {
            const id = crypto.randomUUID();
            set(state => {
                state.flyCues.push({
                    id,
                    sceneId: cue.sceneId || '',
                    cueNumber: cue.cueNumber || '',
                    lineSet: cue.lineSet || '',
                    trimIn: cue.trimIn || '',
                    trimOut: cue.trimOut || '',
                    speed: cue.speed || 'med',
                    customSpeed: cue.customSpeed || '',
                    direction: cue.direction || 'out',
                    notes: cue.notes || '',
                    sortOrder: cue.sortOrder ?? state.flyCues.length,
                    ...cue,
                });
            });
            const scope = getFlyProjectScope(get());
            if (scope) {
                markPendingFlyCueWrite(scope, id);
            }
            return id;
        },
        updateFlyCue: (id, updates) => {
            const scope = getFlyProjectScope(get());
            let didUpdate = false;
            set(state => {
                const idx = state.flyCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    Object.assign(state.flyCues[idx]!, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingFlyCueWrite(scope, id);
            }
        },
        deleteFlyCue: (id) => {
            const scope = getFlyProjectScope(get());
            let didDelete = false;
            set(state => {
                const idx = state.flyCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    state.flyCues[idx]!.deleted = true;
                    state.flyCues[idx]!.deletedAt = Date.now();
                    didDelete = true;
                }
            });
            if (scope && didDelete) {
                markPendingFlyCueWrite(scope, id);
            }
        },
        reorderFlyCues: (sceneId, oldIndex, newIndex) => {
            const scope = getFlyProjectScope(get());
            const reorderedCueIds: string[] = [];
            set(state => {
                const sceneCues = state.flyCues
                    .filter(c => !c.deleted && c.sceneId === sceneId)
                    .toSorted((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                const [moved] = sceneCues.splice(oldIndex, 1);
                sceneCues.splice(newIndex, 0, moved!);

                for (const [i, c] of sceneCues.entries()) {
                    const storeIdx = state.flyCues.findIndex(s => s.id === c.id);
                    if (storeIdx !== -1) {
                        state.flyCues[storeIdx]!.sortOrder = i;
                        reorderedCueIds.push(c.id);
                    }
                }
            });
            if (scope) {
                for (const id of reorderedCueIds) {
                    markPendingFlyCueWrite(scope, id);
                }
            }
        },
        clearFly: () => {
            set(state => { state.flyCues = []; });
        },
    },
});
